const ITEM_TYPE_LABELS: Record<string, string> = {
  weapon: 'Weapon',
  armor: 'Armor',
  accessory: 'Accessory',
  cosmetic: 'Cosmetic',
  material: 'Material',
  misc: 'Misc',
}

const RARITY_LABELS: Record<string, string> = {
  common: 'Common',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
}

const CLASS_LABELS: Record<string, string> = {
  warrior: 'Warrior',
  archer: 'Archer',
  mage: 'Mage',
}

const ITEM_ACQUISITION_LABELS: Record<string, string> = {
  craft: 'Craft',
  drop: 'Drop',
}

const SKILL_TYPE_LABELS: Record<string, string> = {
  auto: 'Auto',
  active: 'Active',
  passive: 'Passive',
}

const SUBTYPE_LABELS: Record<string, string> = {
  two_handed_sword: 'Two-Handed Sword',
  two_handed_axe: 'Two-Handed Axe',
  magic_sword: 'Magic Sword',
  bow: 'Bow',
  crossbow: 'Crossbow',
  staff: 'Staff',
  ring: 'Ring',
  trinket: 'Trinket',
  cloak: 'Cloak',
  artifact: 'Artifact',
}

export const SLOT_TYPE_LABELS: Record<string, string> = {
  SlotWeapon: 'Weapon',
  SlotHelmet: 'Helmet',
  SlotChest: 'Chest',
  SlotBoots: 'Boots',
  SlotGloves: 'Gloves',
  SlotCloak: 'Cloak',
  SlotRing: 'Ring',
  SlotTrinket: 'Trinket',
}

export const INGREDIENT_CATEGORY_LABELS: Record<string, string> = {
  base: 'Base Materials',
  biome: 'Biome Materials',
  dungeon: 'Dungeon Materials',
  weapon_parts: 'Weapon Parts',
}

const STAT_LABELS: Record<string, string> = {
  strength: 'Strength',
  agility: 'Agility',
  intellect: 'Intellect',
  stamina: 'Stamina',
  vitality: 'Vitality',
  willpower: 'Willpower',
  attack_speed: 'Attack Speed',
  critical_strike_chance: 'Crit Chance',
  critical_strike_power: 'Crit Power',
  mana_cost_reduction: 'Mana Cost Reduction',
  movement_speed: 'Movement Speed',
  physical_armor: 'Physical Armor',
  magical_armor: 'Magical Armor',
  health_pool: 'Health',
  mana_pool: 'Mana',
  spell_power: 'Spell Power',
  additional_damage_percentage: 'Bonus Damage',
  cooldown_reduction: 'Cooldown Reduction',
  debuff_protection: 'Debuff Protection',
  dodge_chance: 'Dodge Chance',
  drop_bonus: 'Drop Bonus',
  expirience_precentage: 'Experience Bonus',
  health_regeneration: 'Health Regen',
  magic_resist: 'Magic Resist',
  damage_received_multiplier: 'Damage Taken',
}

const PERCENT_STATS = new Set<string>([
  'additional_damage_percentage',
  'cooldown_reduction',
  'critical_strike_chance',
  'critical_strike_power',
  'dodge_chance',
  'drop_bonus',
  'expirience_precentage',
  'mana_cost_reduction',
  'damage_received_multiplier',
])

const INTERNAL_TAGS = new Set<string>([
  'n',
  'y',
  'material',
  'spareparts',
  'weaponparts',
  'armorparts',
  'dungeonmaterials',
  'bossmaterials',
  'biommaterials',
  'basematerials',
])

const ARMOR_SUFFIXES = ['helmet', 'chest', 'boots', 'gloves', 'cloak']

const SKILL_DAMAGE_TOKEN_LABELS: Record<string, string> = {
  phys: 'Physical',
  physical: 'Physical',
  magic: 'Magic',
  ignoredodge: 'Ignores Dodge',
  ignorephysarmor: 'Ignores Physical Armor',
  ignorephysicalarmor: 'Ignores Physical Armor',
  ignorearmor: 'Ignores Armor',
  ignoremagicarmor: 'Ignores Magic Armor',
  dot: 'Damage over Time',
  crit: 'Critical',
  aoe: 'Area Damage',
}

export const STAT_ORDER = [
  'strength',
  'agility',
  'intellect',
  'stamina',
  'vitality',
  'willpower',
  'physical_armor',
  'magical_armor',
  'health_pool',
  'mana_pool',
  'attack_speed',
  'critical_strike_chance',
  'critical_strike_power',
  'movement_speed',
  'dodge_chance',
  'mana_cost_reduction',
  'health_regeneration',
  'additional_damage_percentage',
  'cooldown_reduction',
  'debuff_protection',
  'drop_bonus',
  'expirience_precentage',
  'magic_resist',
  'spell_power',
  'damage_received_multiplier',
]

function normalizeToken(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function titleCase(value: string) {
  const words = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-]+/g, ' ')
    .trim()
    .split(/\s+/)
  return words
    .map((word) => {
      if (!word) return ''
      return word[0].toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

function formatToken(value: string) {
  if (!value) return ''
  const normalized = value.toLowerCase()
  if (SUBTYPE_LABELS[normalized]) return SUBTYPE_LABELS[normalized]
  return titleCase(normalized)
}

export function formatItemType(value: string) {
  return ITEM_TYPE_LABELS[value] ?? formatToken(value)
}

export function formatRarity(value: string) {
  return RARITY_LABELS[value] ?? formatToken(value)
}

export function formatClass(value: string | null | undefined) {
  if (!value) return ''
  return CLASS_LABELS[value] ?? formatToken(value)
}

export function formatItemAcquisitionSource(value: string | null | undefined) {
  if (!value) return ''
  return ITEM_ACQUISITION_LABELS[value] ?? formatToken(value)
}

export function formatSkillType(value: string) {
  return SKILL_TYPE_LABELS[value] ?? formatToken(value)
}

export function formatWeaponType(value: string) {
  return formatToken(value)
}

export function formatSlotType(value: string) {
  if (!value) return ''
  if (SLOT_TYPE_LABELS[value]) return SLOT_TYPE_LABELS[value]
  if (value.toLowerCase().startsWith('slot')) {
    const stripped = value.slice(4)
    if (SLOT_TYPE_LABELS[`Slot${stripped}`]) return SLOT_TYPE_LABELS[`Slot${stripped}`]
    return titleCase(stripped)
  }
  return formatToken(value)
}

export function formatIngredientCategory(value: string) {
  return INGREDIENT_CATEGORY_LABELS[value] ?? formatToken(value)
}

export function formatItemSubType(value: string) {
  return formatToken(value)
}

export function shouldShowItemSubType(type: string, subType: string) {
  if (!subType) return false
  if (type === 'weapon' || type === 'accessory') return true
  if (type === 'armor') return false
  if (type === 'material' || type === 'cosmetic' || type === 'misc') return false
  const normalized = normalizeToken(subType)
  if (!normalized) return false
  const matchArmor = ARMOR_SUFFIXES.some((suffix) => normalized.endsWith(suffix))
  if (matchArmor) return false
  return true
}

export function isInternalTag(tag: string) {
  const normalized = normalizeToken(tag)
  if (!normalized) return true
  if (INTERNAL_TAGS.has(normalized)) return true
  if (/^\d+$/.test(normalized)) return true
  if (normalized.length <= 1) return true
  return false
}

export function filterDisplayTags(tags?: string[]) {
  if (!tags) return []
  const seen = new Set<string>()
  return tags
    .filter((tag) => tag && !isInternalTag(tag))
    .map((tag) => formatToken(tag))
    .filter((tag) => {
      if (!tag || seen.has(tag)) return false
      seen.add(tag)
      return true
    })
}

export function formatStatKey(key: string) {
  return STAT_LABELS[key] ?? formatToken(key)
}

function formatNumber(value: number) {
  if (Number.isInteger(value)) return `${value}`
  const fixed = value.toFixed(2)
  return fixed.replace(/\.?0+$/, '')
}

export function formatStatValue(
  key: string,
  range: { min: number; max: number },
) {
  const suffix = PERCENT_STATS.has(key) ? '%' : ''
  const min = formatNumber(range.min)
  const max = formatNumber(range.max)
  if (min === max) return `${min}${suffix}`
  return `${min}${suffix}-${max}${suffix}`
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function formatSignedStatValue(key: string, value: number) {
  const formatted = formatStatValue(key, { min: value, max: value })
  return value > 0 ? `+${formatted}` : formatted
}

export interface ItemCardStatLine {
  main: string
  range: string | null
}

export function formatItemCardStatLine(
  key: string,
  stat: { min: number; max: number; rollMin?: number; rollMax?: number },
): ItemCardStatLine {
  const baseValue = isFiniteNumber(stat.min) ? stat.min : 0
  const main = `${formatSignedStatValue(key, baseValue)} ${formatStatKey(key)}`

  const hasRange = isFiniteNumber(stat.rollMin) && isFiniteNumber(stat.rollMax)
  if (!hasRange) {
    return { main, range: null }
  }

  const rollMin = stat.rollMin as number
  const rollMax = stat.rollMax as number
  const left = formatStatValue(key, { min: rollMin, max: rollMin })
  const right = formatStatValue(key, { min: rollMax, max: rollMax })
  return { main, range: `(${left}-${right})` }
}

export interface ParsedSkillDamageDetails {
  damageLine: string
  isAoe: boolean
  isDot: boolean
  extraTags: string[]
}

interface SkillDamageDetailsOptions {
  skillClass?: string | null
  description?: string | null
  effectMeta?: {
    tickSec?: number | null
    isDot?: boolean
    isAoe?: boolean
    dotDamageExpr?: string | null
  } | null
}

const DOT_SKILL_CLASS_HINTS = new Set<string>([
  'dotskill',
  'aoeskillwithdot',
  'iceblock',
  'dragonbreath',
  'arcanebarrage',
  'arcanebarrageunit',
])

export function parseSkillDamageDetails(
  damageRaw: string | null | undefined,
  options?: SkillDamageDetailsOptions,
): ParsedSkillDamageDetails | null {
  const fallbackDotDamage = (options?.effectMeta?.dotDamageExpr || '').trim()
  const source = [damageRaw || '', fallbackDotDamage].filter(Boolean).join(', ')
  if (!source) return null

  const formatDamageToken = (value: string) => {
    const normalized = normalizeToken(value)
    if (!normalized) return ''
    if (SKILL_DAMAGE_TOKEN_LABELS[normalized]) return SKILL_DAMAGE_TOKEN_LABELS[normalized]
    return titleCase(value)
  }

  const parts = source.replace(/\u00a0/g, ' ').split(/[,;]+/).map(p => p.trim()).filter(Boolean)

  const damageParts: string[] = []
  let isAoe = !!options?.effectMeta?.isAoe
  let isDot = !!options?.effectMeta?.isDot
  if (typeof options?.effectMeta?.tickSec === 'number' && options.effectMeta.tickSec > 0) {
    isDot = true
  }

  const classHint = normalizeToken(options?.skillClass || '')
  if (classHint && DOT_SKILL_CLASS_HINTS.has(classHint)) {
    isDot = true
  }
  if (classHint.includes('aoe')) {
    isAoe = true
  }

  const description = (options?.description || '').toLowerCase()
  if (
    /\b(over\s+time|continuous(?:ly)?|periodic(?:al)?|per\s+second|each\s+second|burning)\b/.test(
      description,
    )
  ) {
    isDot = true
  }

  const extraTags: string[] = []

  for (const part of parts) {
    const normalized = normalizeToken(part)
    if (normalized === 'aoe') {
      isAoe = true
      continue
    }
    if (normalized === 'dot' || normalized === 'tick' || normalized === 'periodic') {
      isDot = true
      continue
    }
    if (normalized.startsWith('ignore')) {
      extraTags.push(SKILL_DAMAGE_TOKEN_LABELS[normalized] || titleCase(part))
      continue
    }

    // Keep leading numeric values (e.g. "450%") and normalize trailing tokens.
    const match = part.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+)%?)\s*(.*)$/)
    if (match) {
      const amount = match[1]
      const detail = match[2].trim()
      damageParts.push(detail ? `${amount} ${formatDamageToken(detail)}` : amount)
    } else {
      damageParts.push(formatDamageToken(part))
    }
  }

  let damageLine = damageParts.join(', ')
  if (!damageLine && isDot) {
    damageLine = 'Damage over Time'
  }

  if (!damageLine && !isAoe && !isDot && extraTags.length === 0) {
    return null
  }

  return {
    damageLine,
    isAoe,
    isDot,
    extraTags,
  }
}

export function formatSkillDamage(damageRaw: string | null | undefined): string {
  const parsed = parseSkillDamageDetails(damageRaw)
  if (!parsed) return ''

  const allParts = [parsed.damageLine]
  if (parsed.isDot) allParts.push('Damage over Time')
  if (parsed.isAoe) allParts.push('Area Damage')
  allParts.push(...parsed.extraTags)

  return allParts.filter(Boolean).join(', ')
}
